import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Button,
  Card,
  Row,
  Col,
  Dropdown,
  Menu,
  Space,
  DatePicker,
  Typography,
  Tabs,
  Select,
  message,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  FilterOutlined,
  CloseOutlined,
  CheckOutlined,
  HourglassOutlined,
  DownloadOutlined,
  SelectOutlined,
  PrinterOutlined,
  LeftOutlined,
  InfoCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import Dashboard from "../../../layouts/Dashboard";
import ReactLoading from "react-loading";
import moment from "moment";
import useAxios from "../../../hooks/useAxios";
import { formatDateDDMMYYYY } from "../../../utils/commonFunctions";
import { Link } from "react-router-dom";
import { Tag } from 'antd';
import { useHistory } from "react-router-dom";
import swal from "sweetalert";
import { checkPermission } from "../../../utils/checkPermission";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const CentralHrDashboard = () => {
  const api = useAxios();
  const history = useHistory();
  const [canView, setCanView] = useState(true);

  useEffect(() => {
    const check = async () => {
      const role = localStorage.getItem("user.role");
      if (!role) return;
      const allowed = await checkPermission(role, "central_hr_audit_document_module", "view_central_hr_audit_document");
      setCanView(allowed);
    };
    check();
  }, []);

  const [current, setCurrent] = useState(1);
  const [load, setLoad] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState([]);
  const [candidateList, setCandidateList] = useState([]);
  const [count, setCount] = useState(0);
  const [startDate, setStartDate] = useState();
  const [defaultDate, setDefaultDate] = useState([]);
  const [doj, setDoj] = useState([]);
  const [documentStatus, setDocumentStatus] = useState("");
  const [tabCount, setTabCount] = useState([
    { title: "Yet to Review", value: 0 ,info:"No action taken yet on document rejection."},
    { title: "Approved", value: 0,info : "All documents verified and approved." },
    { title: "In Progress", value: 0,info:"Audit partially done; documents pending." },
    { title: "Confirmation Pending", value: 0 ,info : "Waiting for Site HR's action or candidate's reupload."},
    { title: "Reaudit Pending", value: 0,info : "Documents reuploaded and need reauditing." },
    { title: "Reverification Pending", value: 0, info : "Site HR disagreed with rejection; needs review." },
  ]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Emp ID",
      dataIndex: "empId",
      key: "empId",
      sorter: (a, b) => a.empId.localeCompare(b.empId),
    },
    {
      title: "Date of Joining",
      dataIndex: "doj",
      key: "doj",
      sorter: (a, b) =>
        moment(a.doj, "MMMM DD").unix() -
        moment(b.doj, "MMMM DD").unix(),
    },
    {
      title: "Expd. Closure Date",
      dataIndex: "closureDate",
      key: "closureDate",
      sorter: (a, b) =>
        moment(a.closureDate, "MMMM DD").unix() -
        moment(b.closureDate, "MMMM DD").unix(),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      sorter: (a, b) => a?.location?.localeCompare(b?.location),
    },
    {
      title: "Audit Owner",
      dataIndex: "auditOwner",
      key: "auditOwner",
      render: (value, record) => (
        <span style={{ textDecoration: "underline" }}>{value}</span>
      ),
      sorter: (a, b) => a?.auditOwner?.localeCompare(b?.auditOwner),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Emp. Status",
      dataIndex: "emp_status",
      key: "emp_status",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (value, record) => {
        if (!canView) return null;
        return (
          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (!record?.empId) {
                return message.warning(`Emp Id not available`, 3);
              }
              history.push(
                `/audit/central-hr/document/${record?.empId}/${record?._id}`
              );
            }}
            // to={{
            //   pathname: `/audit/document/${record?.empId}/${record?._id}`,
            //   state: {
            //     empId: record?.empId,
            //   },
            // }}
          >
            <EyeOutlined style={{ fontSize: "18px", color: "black" }} />
          </span>
        );
      },
    },
  ];

  const onChange = (page) => setCurrent(page);

  const getLocations = async () => {
    try {
      const res = await api.get("/location", {
        tokenType: "user",
      });
      setLocations(res.data.locationMaster);
    } catch (err) {
      console.log(err);
    }
  };

  const getTabCount = async () => {
    try {
      const result = await api.get(`/makercheaker/checker/countdocuments`, {
        tokenType: "user",
      });
      const data = result.data;
      setTabCount([
        ...data?.map((ele) => {
          return {
            title: ele?.currentReviewStatusByChecker,
            value: ele?.count || 0,
            info : tabCount?.find(item => item?.title?.toLocaleLowerCase() === ele?.currentReviewStatusByChecker?.toLocaleLowerCase())?.info
          };
        }),
      ]);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    Promise.all([getLocations(), getTabCount()]);
  }, []);

  const generateData = (apiData) => {
    let data = [];
    for (let i = 0; i < apiData?.length; i++) {
      const { makerCheakerAuditingDetails, makercheckerDetails, auditiorUserDetails } = apiData[i];
      const auditor = auditiorUserDetails?.name || "";
      const extraStatus = (makercheckerDetails[0]?.submitedStatusByCheker && makercheckerDetails[0]?.pendingDocumentCount === 1) ? "In Progress" : ""
      const allStatus =
        makerCheakerAuditingDetails?.length !== 0
          ? [
              ...new Set(
                makerCheakerAuditingDetails
                  ?.filter((ele) => ele?.currentReviewStatusByChecker !== "")
                  .map((ele) => ele?.currentReviewStatusByChecker)
              ), extraStatus
            ].filter(Boolean).join(" / ")
          : "Yet to Review";
      let formattedDate = "N/A";
      if (makercheckerDetails?.[0]?.auditEndDate) {
        formattedDate = moment(makercheckerDetails?.[0]?.auditEndDate).format(
          "MMMM D, YYYY"
        );
      }

      const rawDate = apiData[i]?.DateOfJoining;
      const isActive = apiData[i]?.IsCandidateActive;
      const DOJ = rawDate ? moment(apiData[i]?.DateOfJoining).format("MMMM D, YYYY") : "N/A"
      const NAME_DESIGNATION = `${apiData[i]?.name}`
      // const NAME_DESIGNATION = `${apiData[i]?.name} (${apiData[i]?.designations?.designation || "N/A"})`

      data.push({
        name: NAME_DESIGNATION,
        empId: apiData[i]?.empId,
        closureDate: formattedDate,
        doj : DOJ,
        auditOwner: auditor,
        status: allStatus,
        emp_status : <Tag color={isActive ? 'success' : 'error'}>{isActive ? 'Active' : 'Inactive'}</Tag>, 
        location: apiData[i]?.trf?.location,
        _id: apiData[i]?._id,
      });
    }
    setCandidateList([...data]);
  };

  const loadCandidates = async (page = 0) => {
    try {
      setLoad(true);
      const result = await api.get(
        `/makercheaker/checker/CandidateList?PageNo=${
          page ? page : current
        }&PageSize=10&Search=${searchText.trim()}&formId=&location=${location}&statusReview=${documentStatus}&StartDate=${
          defaultDate[0] ? defaultDate[0]?._i : ""
        }&EndDate=${defaultDate[1] ? defaultDate[1]?._i : ""}&DOJStartDate=${
          doj[0] ? doj[0]?._i : ""
        }&DOJEndDate=${doj[1] ? doj[1]?._i : ""}`,
        {
          tokenType: "user",
        }
      );
      const { candidateDocumentDetails, candidateDocumentDetailsCount } =
        result.data;
      generateData(candidateDocumentDetails);
      setCount(candidateDocumentDetailsCount);
    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    loadCandidates();
  }, [location, current, documentStatus, defaultDate,doj]);

  const initialRender = useRef(false);

  useEffect(() => {
    if (!initialRender.current) {
      initialRender.current = true;
      return;
    }
    const timeOut = setTimeout(async () => {
      loadCandidates(1);
    }, 500);

    return () => clearTimeout(timeOut);
  }, [searchText]);

  const handleDate = (value, dateString) => {
    if (value === null) {
      setDefaultDate([]);
    } else {
      let startDate = moment(dateString[0], "YYYY-MM-DD");
      let endDate = moment(dateString[1], "YYYY-MM-DD");
      setDefaultDate([startDate, endDate]);
    }
  };

  const handleCalendarChange = (dates) => {
    if (dates && dates[0]) {
      setStartDate(dates[0]);
    }
  };

  function disableFutureDates(current) {
    const maxDate = startDate
      ? moment(startDate).add(3, "month")
      : moment().endOf("day");
    return current && (current > moment().endOf("day") || current > maxDate);
  }

    const handleDateDOJ = (value, dateString) => {
    if (value === null) {
      setDoj([]);
    } else {
      let startDate = moment(dateString[0], "YYYY-MM-DD");
      let endDate = moment(dateString[1], "YYYY-MM-DD");

      if (moment(endDate).isAfter(moment(startDate).add(60, "days"))) {
        swal({
          title: "You can not select data more than 60 days",
          icon: "error"
        });
      } else {
        setDoj([startDate, endDate]);
      }
    }
  };

  if (!canView) {
    return (
      <Dashboard title="Documents">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Title level={3}>Access Denied</Title>
        </div>
      </Dashboard>
    );
  }

  return (
    <Dashboard title="Documents">
      {load && (
        <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
      )}
      <div className="d-flex justify-content-between align-items-center py-1 mb-3">
        <h4 className="fw-normal">Document Review</h4>
      </div>

      <div className="p-4 bg-white rounded">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(215px, 1fr))`,
            gap: "1rem",
          }}
          className="mb-4"
        >
          {tabCount.map((status) => (
            <div
              style={{
                width: "100%",
              }}
              key={status.title}
              onClick={() =>
                documentStatus === status.title
                  ? setDocumentStatus("")
                  : setDocumentStatus(status.title)
              }
            >
              <Card
                bordered
                style={{
                  borderColor:
                    documentStatus === status?.title ? "#0B56AD" : "#7D7C7C",
                  padding: "7px",
                  height: "80px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  background:
                    documentStatus === status?.title ? "#EBF4FF" : "#fff",
                }}
                bodyStyle={{ padding: 8 }}
              >
                <div className="d-flex gap-1 align-items-center">
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: "bold",
                      textDecoration: "none",
                    }}
                  >
                    {status.title}
                  </Text>
                  <Tooltip placement="top" title={status.info}>
                    <InfoCircleOutlined
                      style={{
                        color: "black",
                        fontSize: "1.1rem",
                      }}
                    />
                  </Tooltip>
                </div>
                <div style={{ fontSize: 24, fontWeight: 800 }}>
                  {status.value}
                </div>
              </Card>
            </div>
          ))}
        </div>

        <div className="flex  align-items-center justify-content-between mb-2">
          <Title level={4} className="m-0">
            {documentStatus !== "" ? documentStatus : "All Application"} -{" "}
            {count}
          </Title>

          <div className="flex flex-col md:flex-row items-center gap-2">
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: 180,
                border: "1px solid #000",
                borderRadius: "6px",
              }}
            />
              <RangePicker
                format="YYYY-MM-DD"
                style={{
                border: "1px solid #000",
                borderRadius: "6px",
                width: "200px",
              }}
              size="middle"
                onChange={handleDateDOJ}
                disabledDate={disableFutureDates}
                placeholder={[
                  "Date of",
                  "Joining"
                ]}
              />
            <RangePicker
              placeholder={['Closure','Date']}
              style={{
                border: "1px solid #000",
                borderRadius: "6px",
                width: "200px",
              }}
              size="middle"
              format="YYYY-MM-DD"
              onChange={handleDate}
              onCalendarChange={handleCalendarChange}
              // disabledDate={disableFutureDates}
            />
            <Select
              showSearch
              className="filter-group-select p-0 outline-none bg-white cutom_select increase_drop"
              placeholder="Select Location"
              maxTagCount={1}
              value={location || null}
              optionFilterProp="children"
              filterOption={(input, option) => {
                return (
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0 ||
                  option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              onChange={(value) => {
                setCurrent(1);
                setLocation(value);
              }}
            >
              <Option style={{ background: "#80808047" }} value={""}>
                Select Location
              </Option>
              {locations
                ?.sort((a, b) =>
                  a.locationName
                    ?.trim()
                    .toLowerCase()
                    .localeCompare(b.locationName?.trim().toLowerCase())
                )
                ?.map((loc) => {
                  if (!loc.status) return;
                  return (
                    <Option key={loc?._id} value={loc?.locationName?.trim()}>
                      {loc?.locationName?.trim()}
                    </Option>
                  );
                })}
            </Select>
          </div>
        </div>
        <div style={{ border: "1.5px solid #0B56AD" }}></div>

        <Table
          columns={columns}
          dataSource={candidateList}
          pagination={{
            total: count,
            pageSize: 10,
            current: current,
            onChange: onChange,
            showSizeChanger: false,
          }}
          scroll={{ x: true }}
          showSorterTooltip={false}
          size="small"
        />
      </div>
    </Dashboard>
  );
};

export default CentralHrDashboard;
